import _ from 'lodash'
import menuService from '@/menu/menu-service'
import settingsService from '@/location-settings/location-settings-service'
import employmentService from '@/employment/employment-service'
import { Diff, diffObjects } from 'shared-js/object-differ'
import { cleanMenu } from 'shared-js/references/menu/clean-menu'
import { cleanLocationSettings } from 'shared-js/references/location-settings/clean-location-settings'
import { cleanEmployment } from 'shared-js/references/employment/clean-employment'
import { cleanAdded } from 'shared-js/utils/company-blobs'
import { deepClone } from '@/common/utils/deep-clone'
import Vue from 'vue'
import { dehydrateMenu, rehydrateMenu } from './menu-hydration'

export class PluConflictError extends Error {
  constructor () {
    super('PLU_VERSION_CONFLICT')
    this.name = 'PluConflictError'
  }
}

export class SettingsFactory {
  static instance

  state = Vue.observable({
    menuId: null,
    locationId: null,
    companyId: null,
    isOverride: false,
    // TPOS-10162: the locationId getLocationSettings() fetched this cached state for, captured
    // once at load time so saveLocationSettings() can guard against a location switch mid-session.
    loadedLocationId: null
  })

  menu
  originalMenuBlob
  menuBase
  menuDiff
  menuGroupDiff

  locationSettings
  originalSettingsBlob
  locationSettingsBase
  locationSettingsGroupDiff
  locationSettingsDiff

  employment
  originalEmploymentBlob
  employmentBase
  employmentDiff

  retrievingSettings = false
  retrieveSettingsQueue = []

  retrievingEmployment = false
  retrieveEmploymentQueue = []

  reset () {
    this.menuId = null
    this.resetMenu()
    this.resetLocationSettings()
    this.resetEmployment()
  }

  resetLocationSettings () {
    this.locationSettings = null
    this.originalSettingsBlob = null
    this.locationSettingsBase = null
    this.locationSettingsDiff = null
    this.locationSettingsGroupDiff = null
    // TPOS-10162: clear the load-time snapshot too, so a stale value can't linger past a reset.
    this.state.loadedLocationId = null
  }

  resetMenu () {
    this.menu = null
    this.originalMenuBlob = null
    this.menuBase = null
    this.menuDiff = null
    this.menuGroupDiff = null
  }

  resetEmployment () {
    this.employment = null
    this.originalEmploymentBlob = null
    this.employmentBase = null
    this.employmentDiff = null
  }

  get menuId () {
    return this.state.menuId
  }

  set menuId (menuId) {
    this.state.menuId = menuId
  }

  get locationId () {
    return this.state.locationId
  }

  set locationId (locationId) {
    this.state.locationId = locationId
    this.calculateIsOverride()
  }

  // TPOS-10162: for callers that legitimately re-point an already-loaded factory at a new
  // location (e.g. licensing/subscriptions save right after a location is first created) rather
  // than a location switch mid-session. Moves loadedLocationId with it so saveLocationSettings()'s
  // mismatch guard doesn't treat the re-target itself as the race it's meant to catch.
  adoptLocation (locationId) {
    this.locationId = locationId
    this.state.loadedLocationId = locationId
  }

  get companyId () {
    return this.state.companyId
  }

  set companyId (companyId) {
    this.state.companyId = companyId
  }

  get isOverride () {
    return this.state.isOverride
  }

  set menuBlob (blob) {
    this.menu.menuBlob = blob
  }

  set menuBlobBase (blob) {
    this.menuBase.menuBlob = blob
  }

  get menuBlob () {
    return _.get(this.menu, 'menuBlob', null)
  }

  get menuBlobBase () {
    return _.get(this.menuBase, 'menuBlob', null)
  }

  get settingsBlob () {
    return _.get(this.locationSettings, 'settingsBlob', null)
  }

  get settingsBlobBase () {
    return _.get(this.locationSettingsBase, 'settingsBlob', null)
  }

  get employmentBlob () {
    return _.get(this.employment, 'employmentBlob', null)
  }

  get employmentBlobBase () {
    return _.get(this.employmentBase, 'employmentBlob', null)
  }

  calculateIsOverride () {
    this.state.isOverride = !_.isEmpty(this.locationId)
  }

  async retrieveSettings () {
    if (this.retrievingSettings) {
      await new Promise(resolve => this.retrieveSettingsQueue.push(resolve))
    }

    this.retrievingSettings = true

    let menuChanges, settingsChanges

    const menuPromise = this.getMenu()
      .then(result => (menuChanges = result !== 304))

    const settingsPromise = this.getLocationSettings()
      .then(result => (settingsChanges = result !== 304))

    let errorCaught = false
    await Promise.all([menuPromise, settingsPromise])
      .catch((error) => {
        errorCaught = error.message === '500'
      })
    if (errorCaught) {
      this.retrievingSettings = false
      return Promise.reject(new Error('Failed to retrieve settings.'))
    }

    if (settingsChanges) {
      cleanLocationSettings(this.settingsBlob)
    }

    if (menuChanges) {
      // pass 1 if isOverride so cleaners can know if it's really forPublish or just isOverride...
      cleanMenu(this.menuBlob, this.settingsBlob, this.isOverride && 1)
    }

    const next = this.retrieveSettingsQueue.shift()
    if (!next) {
      this.retrievingSettings = false
    } else {
      next()
    }
  }

  async retrieveEmployment () {
    if (this.retrievingEmployment) {
      await new Promise(resolve => this.retrieveEmploymentQueue.push(resolve))
    }

    this.retrievingEmployment = true

    let employmentChanges

    const employmentPromise = this.getEmployment()
      .then(result => (employmentChanges = result !== 304))

    await employmentPromise

    if (employmentChanges) {
      cleanEmployment(this.employmentBlob)
    }

    const next = this.retrieveEmploymentQueue.shift()
    if (!next) {
      this.retrievingEmployment = false
      return this.employmentBlob
    } else {
      next()
    }
  }

  async getMenu () {
    if (!this.menuId) {
      return 304
    }

    let response
    if (this.isOverride) {
      const expected = {
        expectedBaseId: _.get(this, 'menu.id'),
        expectedBaseVer: _.get(this, 'menu.ver'),
        expectedOverrideId: _.get(this, 'menu.overrideId'),
        expectedOverrideVer: _.get(this, 'menu.overrideVer')
      }
      response = await menuService.getLocationMenu(this.companyId, this.locationId, expected)
    } else {
      const expectedVersion = _.get(this, 'menu.ver')
      response = await menuService.getCompanyMenu(this.companyId, this.menuId, expectedVersion)
    }

    if (response === 304) {
      return 304
    }

    if (this.isOverride) {
      if (response.diff) {
        this.menuDiff = response.diff
      }
      if (response.groupDiff) {
        this.menuGroupDiff = response.groupDiff
      }

      if (_.get(response, 'base.menuBlob', null)) {
        this.menuBase = response.base
        if (this.menuGroupDiff) {
          const groupDiff = Diff.fromJSON(this.menuGroupDiff)
          groupDiff.changes = groupDiff.changes.filter(c => c.fieldName !== 'pluNumbers')
          this.menuBase.menuBlob = groupDiff.applyChanges(this.menuBase.menuBlob)
          cleanAdded(this.menuBase.menuBlob)
        }
        rehydrateMenu(this.menuBase.menuBlob)
      }

      this.menu = deepClone(this.menuBase)
      const locationDiff = Diff.fromJSON(this.menuDiff)
      locationDiff.changes = locationDiff.changes.filter(c => c.fieldName !== 'pluNumbers')
      this.menu.menuBlob = locationDiff.applyChanges(this.menu.menuBlob)
    } else {
      this.menu = response
      rehydrateMenu(this.menu.menuBlob)
    }

    this.originalMenuBlob = deepClone(this.menu.menuBlob)
  }

  async getLocationSettings () {
    // TPOS-10162 PR review: capture the id this fetch is FOR before the await below, rather than
    // re-reading the live this.locationId once the response comes back. this.locationId can be
    // mutated by a location switch while the request is in flight; re-reading it at the end would
    // stamp loadedLocationId with the NEW location while this.locationSettings still holds the
    // OLD location's data, defeating saveLocationSettings()'s guard the same way the race this
    // ticket fixes did. location-settings-store.js's retrieveLocationSettings already gets this
    // right by capturing once, up front — this mirrors that.
    const requestedLocationId = this.locationId
    let response
    if (this.isOverride) {
      const expected = {
        expectedBaseId: _.get(this, 'locationSettings.id'),
        expectedBaseVer: _.get(this, 'locationSettings.ver'),
        expectedOverrideId: _.get(this, 'locationSettings.overrideId'),
        expectedOverrideVer: _.get(this, 'locationSettings.overrideVer')
      }
      response = await settingsService.getLocationSettings(requestedLocationId, expected)
    } else {
      const expectedVersion = _.get(this, 'locationSettings.ver')
      const expectedId = _.get(this, 'locationSettings.id')
      response = await settingsService.getCompanySettings(this.companyId, expectedVersion, expectedId)
    }

    if (response === 304) {
      return 304
    }

    if (this.isOverride) {
      if (response.diff) {
        this.locationSettingsDiff = response.diff
      }
      if (response.groupDiff) {
        this.locationSettingsGroupDiff = response.groupDiff
      }

      if (_.get(response, 'base.settingsBlob', null)) {
        this.locationSettingsBase = response.base
      }

      // Apply group diff to base
      if (this.locationSettingsGroupDiff) {
        this.locationSettingsBase.settingsBlob = Diff
          .fromJSON(this.locationSettingsGroupDiff)
          .applyChanges(this.locationSettingsBase.settingsBlob)
      }
      cleanAdded(this.locationSettingsBase.settingsBlob)

      this.locationSettings = deepClone(this.locationSettingsBase)

      this.locationSettings.settingsBlob = Diff
        .fromJSON(this.locationSettingsDiff)
        .applyChanges(this.locationSettings.settingsBlob)
    } else {
      this.locationSettings = response
    }

    this.originalSettingsBlob = deepClone(this.locationSettings.settingsBlob)
    this.state.loadedLocationId = requestedLocationId
  }

  async getEmployment () {
    // TPOS-10162 PR review: capture the id this fetch is FOR before the await below, same
    // reasoning as getLocationSettings() above — this.locationId can change while the request is
    // in flight. loadedLocationId is shared across the settings and employment load paths, so
    // this must also be the value saveEmployment()'s guard compares against once this resolves.
    const requestedLocationId = this.locationId
    let response
    if (this.isOverride) {
      const expected = {
        expectedBaseId: _.get(this, 'employment.id'),
        expectedBaseVer: _.get(this, 'employment.ver'),
        expectedOverrideId: _.get(this, 'employment.overrideId'),
        expectedOverrideVer: _.get(this, 'employment.overrideVer')
      }
      response = await employmentService.getLocationEmployment(requestedLocationId, expected)
    } else {
      const expectedVersion = _.get(this, 'employment.ver')
      const expectedId = _.get(this, 'employment.id')
      response = await employmentService.getCompanyEmployment(this.companyId, expectedVersion, expectedId)
    }

    if (response === 304) {
      return 304
    }

    if (this.isOverride) {
      if (response.diff) {
        this.employmentDiff = response.diff
      }
      if (response.groupDiff) {
        this.employmentGroupDiff = response.groupDiff
      }

      if (_.get(response, 'base.employmentBlob', null)) {
        this.employmentBase = response.base
      }

      // Apply group diff to base
      if (this.employmentGroupDiff) {
        this.employmentBase.employmentBlob = Diff
          .fromJSON(this.employmentGroupDiff)
          .applyChanges(this.employmentBase.employmentBlob)
      }
      cleanAdded(this.employmentBase.employmentBlob)

      this.employment = deepClone(this.employmentBase)
      this.employment.employmentBlob = Diff
        .fromJSON(this.employmentDiff)
        .applyChanges(this.employment.employmentBlob)
    } else {
      this.employment = response
    }

    this.originalEmploymentBlob = deepClone(this.employment.employmentBlob)
    this.state.loadedLocationId = requestedLocationId
  }

  async saveMenu () {
    let overrideVer
    const singleUse = this.isOverride && this.menu.singleUse
    if (this.isOverride && !this.menu.singleUse) {
      overrideVer = this.menu.overrideVer

      let dryBaseMenuBlob = dehydrateMenu(deepClone(this.menuBlobBase))
      let dryMenuBlob = dehydrateMenu(deepClone(this.menuBlob))

      this.menuDiff = diffObjects(dryBaseMenuBlob, dryMenuBlob)
      this.menuDiff.pluNumbers = dryMenuBlob.pluNumbers
      const locationResult = await menuService.saveLocationMenu(this.companyId, this.locationId, this.menuDiff, overrideVer)
      this.menu.overrideVer++
      if (locationResult.pluVer != null && this.menuBlob.pluNumbers) {
        this.menuBlob.pluNumbers._ver = locationResult.pluVer
      }
    } else {
      let copy = deepClone(this.menu)
      cleanAdded(copy.menuBlob)
      if (singleUse) {
        delete copy.locationId
        delete copy.overrideId
        delete copy.overrideVer
        delete copy.groupOverrideId
        delete copy.groupOverrideVer

        overrideVer = this.menu.overrideVer
        this.menuBase = deepClone(this.menu)
        let dryMenuBlob = dehydrateMenu(deepClone(this.menu.menuBlob))
        let dryBaseMenuBlob = dehydrateMenu(deepClone(this.menuBase.menuBlob))
        this.menuDiff = diffObjects(dryBaseMenuBlob, dryMenuBlob)
        menuService.saveLocationMenu(this.companyId, this.locationId, this.menuDiff, overrideVer)
      }
      dehydrateMenu(copy.menuBlob)
      let menuResult
      try {
        menuResult = await menuService.updateCompanyMenu(copy, this.companyId)
      } catch (err) {
        if (err === 409) throw new PluConflictError()
        throw err
      }
      this.menu.ver++
      if (menuResult.pluVer != null && this.menuBlob.pluNumbers) {
        this.menuBlob.pluNumbers._ver = menuResult.pluVer
      }
    }

    this.originalMenuBlob = deepClone(this.menuBlob)

  }

  async saveLocationSettings () {

    const pgUrl = window.location.href
    let pgCompanyId = pgUrl.split('companyId=')[1].split('&')[0]
    if (pgCompanyId && pgCompanyId !== this.state.companyId) {
      throw new Error('Company ID does not match! Not saving changes.')
    }

    // TPOS-10162: the settings cached here may have been loaded for a different location than the
    // one now active (a location switch mid-session). Unlike the company-id check above, this skips
    // silently rather than throwing — it's a background safety guard, not a user-facing error.
    if (this.isOverride && this.state.loadedLocationId !== this.locationId) {
      console.warn(
        `SettingsFactory.saveLocationSettings: skipped — active location (${this.locationId}) ` +
        `does not match the location settings were loaded for (${this.state.loadedLocationId}).`
      )
      // TPOS-10162: signal the skip (rather than a bare resolve) so callers — namely
      // settings-factory-mixin.js's save flow — don't tell the user their changes were saved
      // when nothing was persisted.
      // TPOS-10162 PR review (4th round): tagged with a reason. A bare { skipped: true } was
      // ambiguous with the user-cancelled confirm below, so every skip handler blamed a location
      // change regardless of what actually happened.
      return { skipped: true, reason: 'locationChanged' }
    }

    // TPOS-10162 PR review (comment 835345936): the guard above only covers the override branch.
    // A flip to company level sets locationId = undefined (login-store's SET_ACTIVE_LOCATION), so
    // isOverride goes false and that guard is skipped — while locationSettings and
    // loadedLocationId still describe the location loaded a moment ago. Saving then writes a
    // location-merged blob as the company base, which cascades to every location under it.
    // Mirrors the store-side guard added for the same asymmetry.
    // `!_.isEmpty(...)` rather than `!== null`: a company-level load stamps loadedLocationId to
    // `undefined`, so `!== null` would false-skip every legitimate company-only save.
    if (!this.isOverride && !_.isEmpty(this.state.loadedLocationId)) {
      console.warn(
        `SettingsFactory.saveLocationSettings: skipped — saving at company level, but the cached ` +
        `settings were loaded for location (${this.state.loadedLocationId}).`
      )
      return { skipped: true, reason: 'locationChanged' }
    }

    const diffFromOriginal = diffObjects(this.settingsBlob, this.originalSettingsBlob)
    const numChanges = diffFromOriginal && diffFromOriginal.changes && diffFromOriginal.changes.length
    if (numChanges > 6) {
      if (!window.confirm(`You are attempting to save ${numChanges} changes. Are you sure this is correct?`)) {
        // TPOS-10162 PR review (4th round): the user declined deliberately — callers must not
        // report this as a location change (and generally shouldn't message at all).
        return { skipped: true, reason: 'cancelled' }
      }
    }

    if (this.isOverride) {
      this.locationSettingsDiff = diffObjects(this.settingsBlobBase, this.settingsBlob)
      await settingsService.saveLocationSettings(
        this.locationId, this.locationSettingsDiff, this.state.loadedLocationId)
    } else {
      if (!this.settingsBlob.printingRouting) {
        this.settingsBlob.printingRouting = []
      }
      await settingsService.saveCompanySettings(this.companyId, this.settingsBlob)
    }

    this.originalSettingsBlob = deepClone(this.settingsBlob)

    if (this.isOverride) {
      this.locationSettings.overrideVer++
    } else {
      this.locationSettings.ver++
    }

    // TPOS-10162 PR review (4th round): positively signal success. settings-factory-mixin
    // swallows save errors (handleError, no rethrow) and resolves undefined, so `!result.skipped`
    // could not tell a real save from a failed one — MenuAddForm relied on exactly that check
    // before building a menu against displays it believed were persisted.
    return { saved: true }
  }

  async saveEmployment () {
    // TPOS-10162 PR review: same guard as saveLocationSettings() above — this save path shares
    // the identical mid-session location-switch race and was left unguarded when that fix landed.
    if (this.isOverride && this.state.loadedLocationId !== this.locationId) {
      console.warn(
        `SettingsFactory.saveEmployment: skipped — active location (${this.locationId}) ` +
        `does not match the location employment was loaded for (${this.state.loadedLocationId}).`
      )
      return { skipped: true, reason: 'locationChanged' }
    }

    // TPOS-10162 PR review (comment 835345936): same company-level asymmetry as
    // saveLocationSettings above — the review named only the settings path, but this one has the
    // identical shape, so it is closed here too rather than left as a known twin.
    if (!this.isOverride && !_.isEmpty(this.state.loadedLocationId)) {
      console.warn(
        `SettingsFactory.saveEmployment: skipped — saving at company level, but the cached ` +
        `employment was loaded for location (${this.state.loadedLocationId}).`
      )
      return { skipped: true, reason: 'locationChanged' }
    }

    if (this.isOverride) {
      this.employmentDiff = diffObjects(this.employmentBlobBase, this.employmentBlob)
      await employmentService.saveLocationEmployment(
        this.locationId, this.employmentDiff, this.state.loadedLocationId)
    } else {
      await employmentService.saveCompanyEmployment(this.companyId, this.employmentBlob)
    }

    this.originalEmploymentBlob = deepClone(this.employmentBlob)

    if (this.isOverride) {
      this.employment.overrideVer++
    } else {
      this.employment.ver++
    }

    // TPOS-10162 PR review (4th round): see saveLocationSettings above — positive success signal.
    return { saved: true }
  }

  revertMenuChanges () {
    if (this.menu) {
      this.menu.menuBlob = deepClone(this.originalMenuBlob)
    }
  }

  revertLocationSettingsChanges () {
    if (this.locationSettings) {
      this.locationSettings.settingsBlob = deepClone(this.originalSettingsBlob)
    }
  }

  revertEmploymentChanges () {
    if (this.employment) {
      this.employment.employmentBlob = deepClone(this.originalEmploymentBlob)
    }
  }
}
